import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  TrendingUp, 
  TrendingDown,
  Minus,
  Sparkles,
  Calendar,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import { generateEvolutionInsights } from '@/utils/checkinEvolution';

/**
 * Check-in Evolution Component
 * Shows week-over-week progress to highlight improvements and maintain motivation
 */

const CheckinEvolution = ({ asWidget = false }) => {
  const navigate = useNavigate();
  const [evolution, setEvolution] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvolution();
  }, []);

  const loadEvolution = () => {
    setLoading(true);
    
    setTimeout(() => {
      // In production, fetch real 2-week data from backend
      const insights = generateEvolutionInsights();
      setEvolution(insights);
      setLoading(false);
    }, 800);
  };

  if (loading) {
    return (
      <Card className="border-2 border-purple-200 shadow-lg">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
          <p className="text-center text-gray-600 mt-4">
            Comparing your weeks...
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!evolution) {
    return null;
  }

  // Render trend icon
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'improved':
        return <TrendingUp className="h-5 w-5 text-green-600" />;
      case 'declined':
        return <TrendingDown className="h-5 w-5 text-orange-600" />;
      default:
        return <Minus className="h-5 w-5 text-blue-600" />;
    }
  };

  // Render trend badge
  const getTrendBadge = (trend) => {
    const badges = {
      improved: <Badge className="bg-green-100 text-green-800 border-green-300">Improving</Badge>,
      maintained: <Badge className="bg-blue-100 text-blue-800 border-blue-300">Steady</Badge>,
      declined: <Badge className="bg-orange-100 text-orange-800 border-orange-300">Focus Area</Badge>
    };
    return badges[trend] || badges.maintained;
  };

  // Widget version (compact)
  if (asWidget) {
    return (
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              <span className="text-lg">Your Progress</span>
            </div>
            <span className="text-2xl">{evolution.summary.emoji}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-purple-900 font-medium mb-4">
            {evolution.summary.text}
          </p>
          
          {evolution.improvements.length > 0 && (
            <div className="space-y-2">
              {evolution.improvements.slice(0, 2).map((item, index) => (
                <div key={index} className="flex items-start gap-2 bg-white/70 rounded-lg p-3 border border-green-200">
                  <span className="text-xl flex-shrink-0">{item.emoji}</span>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{item.message}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{item.detail}</p>
                  </div>
                  <TrendingUp className="h-4 w-4 text-green-600 flex-shrink-0" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Full page version
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 pb-20">
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center mb-3">
            <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-full p-3">
              <TrendingUp className="h-8 w-8 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            Your Check-in Evolution
          </h1>
          <p className="text-gray-600">
            This week compared to last week
          </p>
          
          <div className="flex items-center justify-center gap-4 text-sm text-gray-500 mt-4">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>Last 14 days</span>
            </div>
          </div>
        </div>

        {/* Summary Card */}
        <Card className={`border-2 shadow-xl ${
          evolution.summary.type === 'excellent' ? 'border-green-300 bg-gradient-to-br from-green-50 to-emerald-50' :
          evolution.summary.type === 'good' ? 'border-blue-300 bg-gradient-to-br from-blue-50 to-cyan-50' :
          evolution.summary.type === 'positive' ? 'border-purple-300 bg-gradient-to-br from-purple-50 to-pink-50' :
          'border-gray-300 bg-gradient-to-br from-gray-50 to-slate-50'
        }`}>
          <CardContent className="pt-8 pb-6">
            <div className="text-center">
              <span className="text-6xl mb-4 block">{evolution.summary.emoji}</span>
              <p className="text-2xl font-bold text-gray-900 mb-2">
                {evolution.summary.text}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Improvements Section */}
        {evolution.improvements.length > 0 && (
          <Card className="border-2 border-green-200 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-900">
                <Sparkles className="h-6 w-6 text-green-600" />
                What's Improving
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {evolution.improvements.map((item, index) => (
                  <div key={index} className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                    <div className="flex items-start gap-3">
                      <div className="bg-white rounded-full p-2 flex-shrink-0">
                        <span className="text-2xl">{item.emoji}</span>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-gray-900">{item.message}</h3>
                          {getTrendIcon(item.trend)}
                        </div>
                        <p className="text-sm text-gray-700">{item.detail}</p>
                      </div>
                      
                      {getTrendBadge(item.trend)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Maintained Section */}
        {evolution.maintained.length > 0 && (
          <Card className="border-2 border-blue-200 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <Minus className="h-6 w-6 text-blue-600" />
                Staying Consistent
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-3">
                {evolution.maintained.map((item, index) => (
                  <div key={index} className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">{item.emoji}</span>
                      <span className="text-sm font-medium text-gray-900">{item.message}</span>
                    </div>
                    <p className="text-xs text-gray-600 ml-7">{item.detail}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Focus Areas (Declines - supportive language) */}
        {evolution.declines.length > 0 && (
          <Card className="border-2 border-amber-200 bg-amber-50 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-900">
                <TrendingDown className="h-6 w-6 text-amber-600" />
                Areas to Focus On
              </CardTitle>
              <p className="text-sm text-amber-800 mt-2">
                It happens! Let's work on these together.
              </p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {evolution.declines.map((item, index) => (
                  <div key={index} className="bg-white p-4 rounded-lg border border-amber-200">
                    <div className="flex items-start gap-3">
                      <span className="text-2xl flex-shrink-0">{item.emoji}</span>
                      <div className="flex-1">
                        <h3 className="font-medium text-gray-900 mb-1">{item.message}</h3>
                        <p className="text-sm text-gray-700">{item.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Encouragement */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Sparkles className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-purple-900">
                <strong>Remember:</strong> Progress isn't always linear. Some weeks are better than others, 
                and that's completely normal. What matters is showing up and learning about your patterns.
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid sm:grid-cols-3 gap-4">
          <Button
            onClick={loadEvolution}
            variant="outline"
            className="border-2 border-purple-300 hover:bg-purple-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          
          <Button
            onClick={() => navigate('/health-story')}
            variant="outline"
            className="border-2 border-blue-300 hover:bg-blue-50"
          >
            Your Health Story
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
          
          <Button
            onClick={() => navigate('/daily-loop')}
            className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
          >
            Track Today
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>

        {/* Back Button */}
        <div className="flex justify-center">
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="border-2 border-gray-300 hover:bg-gray-50"
          >
            Back to Home
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Widget version for Dashboard
 */
export const CheckinEvolutionWidget = () => {
  return <CheckinEvolution asWidget={true} />;
};

export default CheckinEvolution;
